import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';

import gsap, { Power1 } from 'gsap';
import { danger, neutral, success } from 'theme/colors';
import { Wrapper } from './Options.styles';

// valid states
const VALID_STATES = {
  valid: 'valid',
  neutral: 'neutral',
  invalid: 'invalid',
};

const DEFAULT_OPTIONS = [
  {
    label: 'Sim',
    value: 'sim',
  },
  {
    label: 'Não',
    value: 'nao',
  },
];

const Options = forwardRef((props, $ref) => {
  // PROPS
  const {
    label,
    slug,
    required,
    half,
    errorMessage,
    options = DEFAULT_OPTIONS,
  } = props;
  // PROPS

  // STATES
  // toggle validation state
  // 3 states: valid, invalid and neutral
  // mainly registered in label color, border color and icon
  const [valid, setValid] = useState(VALID_STATES.neutral);
  // input value, can have default value
  const [val, setVal] = useState('');
  // STATES

  // REF
  const wrapperRef = useRef();
  const inputRef = useRef();
  const labelRef = useRef();
  // REF

  //ANIMATION
  const animations = {
    focusAnimation: () => {
      if (valid === VALID_STATES.neutral) {
        gsap
          .timeline()
          .to(inputRef.current, {
            borderColor: neutral.neu09,
            duration: 0.15,
          })
          .to(labelRef.current, {
            color: neutral.neu09,
            duration: 0.15,
            delay: -0.15,
          });
      }
      gsap.timeline().to(labelRef.current, {
        top: 0,
        scale: 0.85,
        duration: 0.15,
        ease: Power1.easeOut,
      });
    },
    blurAnimation: () => {
      if (valid === VALID_STATES.neutral) {
        gsap.timeline().to(inputRef.current, {
          borderColor: neutral.neu06,
          duration: 0.15,
        });
      }
      gsap.timeline().to(labelRef.current, {
        top: 27,
        scale: 1,
        duration: 0.15,
        ease: Power1.easeOut,
      });
    },
    neutralAnimation: () => {
      gsap
        .timeline()
        .to(inputRef.current, {
          borderColor: neutral.neu06,
          duration: 0.15,
        })
        .to(labelRef.current, {
          color: neutral.neu06,
          duration: 0.15,
          delay: -0.15,
          ease: Power1.easeOut,
        });
    },
    invalidateAnimation: () => {
      gsap
        .timeline()
        .to(inputRef.current, {
          borderColor: danger.default,
          duration: 0.15,
        })
        .to(labelRef.current, {
          color: danger.default,
          duration: 0.15,
          delay: -0.15,
          ease: Power1.easeOut,
        });
    },
    validateAnimation: () => {
      gsap
        .timeline()
        .to(inputRef.current, {
          borderColor: success.default,
          duration: 0.15,
        })
        .to(labelRef.current, {
          color: success.default,
          duration: 0.15,
          delay: -0.15,
          ease: Power1.easeOut,
        });
    },
  };
  //ANIMATION

  // VALIDATE FUNCTION
  const validate = (text) => {
    if (!val) {
      return VALID_STATES.invalid;
    } else {
      return VALID_STATES.valid;
    }
  };
  // VALIDATE FUNCTION

  // IMPERATIVEHANDLE
  useImperativeHandle(
    $ref,
    () => ({
      value: val,
      valid: valid === VALID_STATES.valid ? true : false,
      forceValidation: () => setValid(validate(val)),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [val, valid]
  );
  // IMPERATIVEHANDLE

  // EFFECTS
  useEffect(() => {
    switch (valid) {
      case VALID_STATES.invalid:
        animations.invalidateAnimation();
        break;
      case VALID_STATES.valid:
        animations.validateAnimation();
        break;
      case VALID_STATES.neutral:
        animations.neutralAnimation();
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid]);

  // EFFECTS
  return (
    <Wrapper half={half} ref={wrapperRef}>
      <div className="options">
        <label htmlFor={slug} ref={labelRef}>
          {label}
          {required && ' *'}
        </label>
        {options.map((option) => {
          return (
            <button
              className={
                val === option.value ? 'option option--active' : 'option'
              }
              type="button"
              onClick={() => {
                setValid(VALID_STATES.valid);
                setVal(option.value);
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {valid === VALID_STATES.invalid && (
        <p className="input__error-msg">{errorMessage(val)}</p>
      )}
    </Wrapper>
  );
});

Options.displayName = 'Options';

export default Options;
